import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FichierPro.css';
import './DataPacPv.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const ITEMS_PER_PAGE = 50;

const statutLabels = {
  nouveau: 'Nouveau',
  a_rappeler: 'A rappeler',
  interesse: 'Intéressé',
  pas_interesse: 'Pas intéressé',
  rdv_pris: 'RDV pris',
  ne_pas_rappeler: 'Ne pas rappeler',
  nrp: 'NRP',
  hors_criteres: 'Ne rentre pas dans les critères',
  faux_num: 'Faux numéro'
};

const civiliteLabels = { M: 'Monsieur', MME: 'Madame', MLLE: 'Mademoiselle' };

const FILTRES_VIDES = {
  departement: '',
  ville: '',
  code_postal: '',
  search: '',
  statut: '',
  source_fichier: '',
  option_tarifaire: '',
  puissance_souscrite: '',
  mode_paiement: '',
  civilite_norm: '',
  canal: '',
  doublon: '',
  conso_min: '',
  conso_max: '',
  cout_min: '',
  cout_max: '',
  cout_periode: 'an',
  age_min: '',
  age_max: '',
  avec_email: false,
  avec_mobile: false,
  avec_tel: false,
  avec_pdl: false,
  avec_conso: false,
  avec_naissance: false
};

const nombre = (v) => (v === null || v === undefined || v === '') ? null : Number(v);

// Les consommations sont stockées avec leurs décimales ; on les affiche en kWh lisibles
const formatConso = (v) => {
  const n = nombre(v);
  if (n === null || Number.isNaN(n)) return null;
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} kWh`;
};

const formatEuros = (v, decimales = 0) => {
  const n = nombre(v);
  if (n === null || Number.isNaN(n)) return null;
  return `${n.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })} €`;
};

const formatDateHeure = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.toLocaleDateString('fr-FR')} ${dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

// Une valeur absente est affichée « Non renseigné » plutôt que masquée : la fiche montre
// toujours l'intégralité des champs du fichier source.
function Champ({ label, value, children }) {
  const vide = children === undefined && (value === null || value === undefined || value === '');
  return (
    <div className="dpv-field">
      <span className="dpv-field-label">{label}</span>
      <span className={`dpv-field-value${vide ? ' vide' : ''}`}>
        {children !== undefined ? children : (vide ? 'Non renseigné' : value)}
      </span>
    </div>
  );
}

function DataPacPv() {
  const [filtres, setFiltres] = useState(FILTRES_VIDES);
  const [tri, setTri] = useState('conso');
  const [showAvances, setShowAvances] = useState(false);

  const [listes, setListes] = useState({
    departements: [], sources: [], options: [], puissances: [], modes: [], civilites: [], canaux: []
  });
  const [couverture, setCouverture] = useState(null);
  const [totalBase, setTotalBase] = useState(0);

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [repartition, setRepartition] = useState({ parDept: [], parStatut: [] });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchDone, setSearchDone] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const [commentEnregistre, setCommentEnregistre] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [tarifs, setTarifs] = useState(null);
  const [showTarifs, setShowTarifs] = useState(false);
  const [tarifsDraft, setTarifsDraft] = useState(null);
  const [savingTarifs, setSavingTarifs] = useState(false);

  const appliquerResultat = (data, targetPage) => {
    setResults(data.results || []);
    setTotal(data.total || 0);
    setPage(data.page || targetPage);
    setTotalPages(data.totalPages || 1);
    setRepartition(data.stats || { parDept: [], parStatut: [] });
    setEstimation(data.estimation || null);
    if (data.tarifs) setTarifs(data.tarifs);
    setSearchDone(true);
  };

  const chargerBase = async (targetPage = 1, filtresUtilises = null, triUtilise = null) => {
    setLoading(true);
    setError('');
    try {
      const body = {
        ...(filtresUtilises || filtres),
        tri: triUtilise || tri,
        page: targetPage,
        limit: ITEMS_PER_PAGE
      };
      const res = await axios.post(`${API_URL}/api/data-pacpv/base`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 30000
      });
      appliquerResultat(res.data, targetPage);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la consultation de la base.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Listes déroulantes + volumétrie de la base
    axios.get(`${API_URL}/api/data-pacpv/filters`, { headers })
      .then(res => {
        setListes({
          departements: res.data.departements || [],
          sources: res.data.sources || [],
          options: res.data.options || [],
          puissances: res.data.puissances || [],
          modes: res.data.modes || [],
          civilites: res.data.civilites || [],
          canaux: res.data.canaux || []
        });
        setCouverture(res.data.couverture || null);
        setTotalBase(res.data.total || 0);
      })
      .catch(err => console.error('Erreur chargement filtres Data PAC/PV:', err));

    // Première page affichée sans filtre, pour que l'onglet ne s'ouvre pas sur un écran vide
    setLoading(true);
    axios.post(`${API_URL}/api/data-pacpv/base`,
      { ...FILTRES_VIDES, tri: 'conso', page: 1, limit: ITEMS_PER_PAGE }, { headers, timeout: 30000 })
      .then(res => {
        setResults(res.data.results || []);
        setTotal(res.data.total || 0);
        setPage(res.data.page || 1);
        setTotalPages(res.data.totalPages || 1);
        setRepartition(res.data.stats || { parDept: [], parStatut: [] });
        setEstimation(res.data.estimation || null);
        if (res.data.tarifs) setTarifs(res.data.tarifs);
        setSearchDone(true);
      })
      .catch(err => setError(err.response?.data?.error || 'Erreur lors de la consultation de la base.'))
      .finally(() => setLoading(false));
  }, []);

  const setF = (champ, valeur) => setFiltres(prev => ({ ...prev, [champ]: valeur }));

  const handleReset = () => {
    setFiltres(FILTRES_VIDES);
    setTri('conso');
    chargerBase(1, FILTRES_VIDES, 'conso');
  };

  const handleChangeTri = (valeur) => {
    setTri(valeur);
    chargerBase(1, null, valeur);
  };

  const handleStatutChange = async (id, statut) => {
    try {
      const res = await axios.post(`${API_URL}/api/data-pacpv/statut`, { id, statut }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const maj = {
        statut,
        statut_par: res.data.statut_par,
        statut_par_nom: res.data.statut_par_nom,
        statut_date: res.data.statut_date
      };
      setResults(prev => prev.map(r => r.id === id ? { ...r, ...maj } : r));
      setFiche(prev => (prev && prev.id === id) ? { ...prev, ...maj } : prev);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du changement de statut');
    }
  };

  // Fermeture protégée : un commentaire saisi mais non enregistré serait perdu sans prévenir.
  const fermerFiche = () => {
    if (commentDraft !== (fiche?.commentaire || '')
        && !window.confirm('Votre commentaire n\'est pas enregistré. Fermer quand même ?')) return;
    setFiche(null);
    setCommentEnregistre(false);
  };

  const openFiche = async (contact) => {
    // Affichage immédiat depuis la ligne du tableau, puis complément par la fiche
    // serveur qui porte le détail chiffré de l'estimation.
    setFiche(contact);
    setCommentDraft(contact.commentaire || '');
    setCommentEnregistre(false);
    try {
      const res = await axios.get(`${API_URL}/api/data-pacpv/${contact.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFiche(prev => (prev && prev.id === contact.id) ? { ...prev, ...res.data } : prev);
    } catch (err) {
      console.error('Erreur chargement fiche Data PAC/PV:', err);
    }
  };

  const ouvrirTarifs = () => {
    setTarifsDraft(JSON.parse(JSON.stringify(tarifs)));
    setShowTarifs(true);
  };

  const enregistrerTarifs = async () => {
    setSavingTarifs(true);
    try {
      const res = await axios.put(`${API_URL}/api/data-pacpv/tarifs`, tarifsDraft, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTarifs(res.data);
      setShowTarifs(false);
      chargerBase(page);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement du barème');
    } finally {
      setSavingTarifs(false);
    }
  };

  const handleSaveComment = async () => {
    if (!fiche) return;
    setSavingComment(true);
    try {
      await axios.post(`${API_URL}/api/data-pacpv/commentaire`, { id: fiche.id, commentaire: commentDraft }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setResults(prev => prev.map(r => r.id === fiche.id ? { ...r, commentaire: commentDraft } : r));
      setFiche(prev => ({ ...prev, commentaire: commentDraft }));
      setCommentEnregistre(true);
      setTimeout(() => setCommentEnregistre(false), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement du commentaire');
    } finally {
      setSavingComment(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtres).forEach(([k, v]) => {
        if (v === true) params.append(k, '1');
        else if (v !== '' && v !== false) params.append(k, v);
      });
      const res = await axios.get(`${API_URL}/api/data-pacpv/export-csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob',
        timeout: 180000
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `data-pacpv-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const adresseComplete = (c) => [
    [c.adresse_num, c.adresse_rue].filter(Boolean).join(' '),
    [c.code_postal, c.ville].filter(Boolean).join(' ')
  ].filter(Boolean).join(', ');

  return (
    <div className="fichier-pro">
      <div className="fichier-pro-header">
        <h2>Data PAC PV</h2>
        <p className="fichier-pro-subtitle">
          Base de données PAC / Photovoltaïque | <strong>{totalBase.toLocaleString('fr-FR')}</strong> contacts
        </p>
        {couverture && (
          <div className="dpv-couverture">
            <span className="dpv-chip">Téléphone <strong>{couverture.avec_tel.toLocaleString('fr-FR')}</strong></span>
            <span className="dpv-chip">Mobile <strong>{couverture.avec_mobile.toLocaleString('fr-FR')}</strong></span>
            <span className="dpv-chip">Email <strong>{couverture.avec_email.toLocaleString('fr-FR')}</strong></span>
            <span className="dpv-chip">N° PDL <strong>{couverture.avec_pdl.toLocaleString('fr-FR')}</strong></span>
            <span className="dpv-chip">Consommation <strong>{couverture.avec_conso.toLocaleString('fr-FR')}</strong></span>
            <span className="dpv-chip">Tél. unique <strong>{couverture.uniques.toLocaleString('fr-FR')}</strong></span>
          </div>
        )}
      </div>

      {/* Filtres principaux */}
      <div className="fichier-pro-filters">
        <div className="filter-group">
          <label>Département</label>
          <select value={filtres.departement} onChange={e => setF('departement', e.target.value)}>
            <option value="">-- Tous --</option>
            {listes.departements.map(d => (
              <option key={d.departement} value={d.departement}>
                {d.departement} - {d.nom_departement} ({d.count})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Ville</label>
          <input type="text" className="filter-input" value={filtres.ville}
            onChange={e => setF('ville', e.target.value)} placeholder="Ville..."
            onKeyDown={e => e.key === 'Enter' && chargerBase(1)} />
        </div>

        <div className="filter-group">
          <label>Code postal</label>
          <input type="text" className="filter-input" value={filtres.code_postal}
            onChange={e => setF('code_postal', e.target.value)} placeholder="Ex: 57 ou 57000"
            onKeyDown={e => e.key === 'Enter' && chargerBase(1)} />
        </div>

        <div className="filter-group">
          <label>Recherche libre</label>
          <input type="text" className="filter-input" value={filtres.search}
            onChange={e => setF('search', e.target.value)}
            placeholder="Nom, prénom, tél, email, PDL, adresse..."
            onKeyDown={e => e.key === 'Enter' && chargerBase(1)} />
        </div>

        <div className="filter-group">
          <label>Statut</label>
          <select value={filtres.statut} onChange={e => setF('statut', e.target.value)}>
            <option value="">-- Tous --</option>
            {Object.entries(statutLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="filter-group">
          <label>Trier par</label>
          <select value={tri} onChange={e => handleChangeTri(e.target.value)}>
            <option value="conso">Consommation la plus forte</option>
            <option value="cout">Facture estimée la plus élevée</option>
            <option value="ancien">Ordre du fichier</option>
            <option value="recent">Dernières fiches</option>
            <option value="nom">Nom (A-Z)</option>
            <option value="ville">Ville (A-Z)</option>
            <option value="age">Âge (du plus âgé)</option>
          </select>
        </div>

        <button className="btn-search" onClick={() => chargerBase(1)} disabled={loading}>
          {loading ? <><span className="spinner-small"></span> Recherche...</> : 'Consulter'}
        </button>

        <button className="btn-search" style={{ background: '#5a6480', marginLeft: '8px' }} onClick={handleReset}>
          Réinitialiser
        </button>

        <button className="btn-search" style={{ background: '#2e7d32', marginLeft: '8px' }}
          onClick={handleExportCSV} disabled={exportLoading}
          title="Exporter en CSV le résultat filtré (toutes les colonnes du fichier + l'estimation)">
          {exportLoading ? <><span className="spinner-small"></span> Export...</> : 'Exporter CSV'}
        </button>

        {tarifs && (
          <button className="btn-search" style={{ background: '#7b2ff7', marginLeft: '8px' }}
            onClick={ouvrirTarifs} title="Régler le prix du kWh et les abonnements servant à l'estimation">
            Barème
          </button>
        )}
      </div>

      <button className="dpv-filters-toggle" onClick={() => setShowAvances(v => !v)}>
        {showAvances ? '− Masquer les filtres avancés' : '+ Filtres avancés (énergie, âge, source)'}
      </button>

      {showAvances && (
        <div className="dpv-filters-avances">
          <div className="filter-group">
            <label>Civilité</label>
            <select value={filtres.civilite_norm} onChange={e => setF('civilite_norm', e.target.value)}>
              <option value="">-- Toutes --</option>
              {listes.civilites.map(c => (
                <option key={c.civilite_norm} value={c.civilite_norm}>
                  {civiliteLabels[c.civilite_norm] || c.civilite_norm} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Option tarifaire</label>
            <select value={filtres.option_tarifaire} onChange={e => setF('option_tarifaire', e.target.value)}>
              <option value="">-- Toutes --</option>
              {listes.options.map(o => (
                <option key={o.option_tarifaire} value={o.option_tarifaire}>{o.option_tarifaire} ({o.count})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Puissance souscrite</label>
            <select value={filtres.puissance_souscrite} onChange={e => setF('puissance_souscrite', e.target.value)}>
              <option value="">-- Toutes --</option>
              {listes.puissances.map(p => (
                <option key={p.puissance_souscrite} value={p.puissance_souscrite}>{p.puissance_souscrite} kVA ({p.count})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Mode de paiement</label>
            <select value={filtres.mode_paiement} onChange={e => setF('mode_paiement', e.target.value)}>
              <option value="">-- Tous --</option>
              {listes.modes.map(m => (
                <option key={m.mode_paiement} value={m.mode_paiement}>{m.mode_paiement} ({m.count})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Canal d'origine</label>
            <select value={filtres.canal} onChange={e => setF('canal', e.target.value)}>
              <option value="">-- Tous --</option>
              {listes.canaux.map(c => (
                <option key={c.canal} value={c.canal}>{c.canal} ({c.count})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Fichier source</label>
            <select value={filtres.source_fichier} onChange={e => setF('source_fichier', e.target.value)}>
              <option value="">-- Tous --</option>
              {listes.sources.map(s => (
                <option key={s.source_fichier} value={s.source_fichier}>{s.source_fichier} ({s.count})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Marquage du fichier d'origine</label>
            <select value={filtres.doublon} onChange={e => setF('doublon', e.target.value)}>
              <option value="">-- Tous --</option>
              <option value="unique">Numéro unique à l'origine</option>
              <option value="doublon">Copie conservée d'un doublon</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Conso. annuelle (kWh)</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input type="number" className="filter-input dpv-mini" placeholder="min" value={filtres.conso_min}
                onChange={e => setF('conso_min', e.target.value)} />
              <input type="number" className="filter-input dpv-mini" placeholder="max" value={filtres.conso_max}
                onChange={e => setF('conso_max', e.target.value)} />
            </div>
          </div>

          <div className="filter-group">
            <label>Facture estimée (€)</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input type="number" className="filter-input dpv-mini" placeholder="min" value={filtres.cout_min}
                onChange={e => setF('cout_min', e.target.value)} />
              <input type="number" className="filter-input dpv-mini" placeholder="max" value={filtres.cout_max}
                onChange={e => setF('cout_max', e.target.value)} />
              <select className="filter-input" style={{ width: '92px' }} value={filtres.cout_periode}
                onChange={e => setF('cout_periode', e.target.value)}>
                <option value="an">par an</option>
                <option value="mois">par mois</option>
              </select>
            </div>
            {(filtres.cout_min || filtres.cout_max) && (
              <small className="dpv-filtre-aide">
                {filtres.cout_periode === 'mois'
                  ? `soit ${filtres.cout_min ? `${Math.round(Number(filtres.cout_min) * 12)} €` : '…'} à ${filtres.cout_max ? `${Math.round(Number(filtres.cout_max) * 12)} €` : '…'} par an`
                  : `soit ${filtres.cout_min ? `${Math.round(Number(filtres.cout_min) / 12)} €` : '…'} à ${filtres.cout_max ? `${Math.round(Number(filtres.cout_max) / 12)} €` : '…'} par mois`}
                {' '}— seules les fiches avec consommation ressortent
              </small>
            )}
          </div>

          <div className="filter-group">
            <label>Âge</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input type="number" className="filter-input dpv-mini" placeholder="min" value={filtres.age_min}
                onChange={e => setF('age_min', e.target.value)} />
              <input type="number" className="filter-input dpv-mini" placeholder="max" value={filtres.age_max}
                onChange={e => setF('age_max', e.target.value)} />
            </div>
          </div>

          <div className="dpv-checks">
            {[
              ['avec_tel', 'Avec téléphone'],
              ['avec_mobile', 'Avec mobile'],
              ['avec_email', 'Avec email'],
              ['avec_pdl', 'Avec n° PDL'],
              ['avec_conso', 'Avec consommation'],
              ['avec_naissance', 'Avec date de naissance']
            ].map(([champ, label]) => (
              <label key={champ} className="dpv-check">
                <input type="checkbox" checked={filtres[champ]} onChange={e => setF(champ, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {error && <div className="fichier-pro-error">{error}</div>}

      {searchDone && !loading && (
        <div className="fichier-pro-results">
          <div className="results-header">
            <span className="results-count">
              {total.toLocaleString('fr-FR')} contact{total !== 1 ? 's' : ''} trouvé{total !== 1 ? 's' : ''}
            </span>
            <div className="results-meta">
              {tri === 'conso' && couverture && (
                <span className="dpv-note-tri">
                  Plus grosses consommations en tête — {couverture.avec_conso.toLocaleString('fr-FR')} fiches ont une consommation connue, les autres suivent
                </span>
              )}
              {totalPages > 1 && <span className="results-page">Page {page} / {totalPages}</span>}
            </div>
          </div>

          {estimation && estimation.fiches_avec_conso > 0 && (
            <div className="dpv-estimation-bandeau">
              <div className="dpv-carte">
                <span className="dpv-carte-label">Fiches chiffrables</span>
                <span className="dpv-carte-valeur">{estimation.fiches_avec_conso.toLocaleString('fr-FR')}</span>
                <span className="dpv-carte-sous">sur {total.toLocaleString('fr-FR')} du résultat</span>
              </div>
              <div className="dpv-carte">
                <span className="dpv-carte-label">Consommation cumulée</span>
                <span className="dpv-carte-valeur">{estimation.conso_totale.toLocaleString('fr-FR')} kWh</span>
                <span className="dpv-carte-sous">{estimation.conso_moyenne.toLocaleString('fr-FR')} kWh en moyenne</span>
              </div>
              <div className="dpv-carte dpv-carte-euro">
                <span className="dpv-carte-label">Facture annuelle cumulée</span>
                <span className="dpv-carte-valeur">{formatEuros(estimation.cout_total)}</span>
                <span className="dpv-carte-sous">estimation, TTC</span>
              </div>
              <div className="dpv-carte dpv-carte-euro">
                <span className="dpv-carte-label">Facture moyenne</span>
                <span className="dpv-carte-valeur">{formatEuros(estimation.cout_moyen)}</span>
                <span className="dpv-carte-sous">soit {formatEuros(Math.round(estimation.cout_moyen / 12))} par mois</span>
              </div>
              <div className="dpv-carte dpv-carte-euro">
                <span className="dpv-carte-label">Facture la plus élevée</span>
                <span className="dpv-carte-valeur">{formatEuros(estimation.cout_max)}</span>
                <span className="dpv-carte-sous">du résultat filtré</span>
              </div>
            </div>
          )}

          {tarifs && (
            <p className="dpv-avertissement">
              Estimation reconstituée à partir de la consommation du fichier et du barème «&nbsp;{tarifs.libelle}&nbsp;»
              ({formatEuros(tarifs.prix_base, 4)}/kWh en Base, {formatEuros(tarifs.prix_hp, 4)} en heures pleines,
              {' '}{formatEuros(tarifs.prix_hc, 4)} en heures creuses, plus l'abonnement selon la puissance).
              Ce ne sont pas des factures réelles : ajustez le barème avec le bouton «&nbsp;Barème&nbsp;».
            </p>
          )}

          {results.length > 0 ? (
            <>
              <div className="table-wrapper">
                <table className="fichier-pro-table dpv-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Ville</th>
                      <th>Téléphone</th>
                      <th>Mobile</th>
                      <th>Conso.</th>
                      <th>Facture estimée</th>
                      <th>Statut</th>
                      <th>Statué par</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(r => (
                      <tr key={r.id}>
                        <td className="td-nom">
                          {[r.civilite, r.nom, r.prenom].filter(Boolean).join(' ') || '-'}
                          {r.age ? <><br /><small style={{ color: '#57627d' }}>{r.age} ans</small></> : null}
                        </td>
                        <td>{r.ville || '-'}{r.code_postal ? ` (${r.code_postal})` : ''}</td>
                        <td className="td-phone">
                          {r.telephone ? <a href={`tel:${r.telephone}`} className="phone-link">{r.telephone}</a> : '-'}
                        </td>
                        <td className="td-phone">
                          {r.tel_portable ? <a href={`tel:${r.tel_portable}`} className="phone-link">{r.tel_portable}</a> : '-'}
                        </td>
                        <td className="dpv-td-conso">
                          {formatConso(r.conso_totale)
                            ? <>
                                <strong>{formatConso(r.conso_totale)}</strong>
                                {r.option_tarifaire ? <><br /><small className="dpv-td-option">{r.option_tarifaire}</small></> : null}
                              </>
                            : <span className="dpv-conso-absente">non renseignée</span>}
                        </td>
                        <td className="dpv-td-conso">
                          {r.cout_annuel_estime !== null && r.cout_annuel_estime !== undefined
                            ? <>
                                <strong className="dpv-euro">{formatEuros(r.cout_annuel_estime)}</strong>
                                <br /><small className="dpv-td-option">{formatEuros(Number(r.cout_annuel_estime) / 12)}/mois</small>
                              </>
                            : <span className="dpv-conso-absente">—</span>}
                        </td>
                        <td className="td-statut">
                          <select className={`statut-select statut-${r.statut || 'nouveau'}`}
                            value={r.statut || 'nouveau'}
                            onChange={e => handleStatutChange(r.id, e.target.value)}>
                            {Object.entries(statutLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </td>
                        <td className="td-statut-par">
                          {r.statut_par_nom && (
                            <span className="statut-par-info">
                              {r.statut_par_nom}<br />
                              <small>{formatDateHeure(r.statut_date)}</small>
                            </span>
                          )}
                        </td>
                        <td className="td-actions">
                          <button className="btn-voir-fiche" onClick={() => openFiche(r)}>
                            Voir la fiche{r.commentaire ? ' 💬' : ''}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => chargerBase(Math.max(1, page - 1))} disabled={page === 1}>Précédent</button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let p;
                    if (totalPages <= 10) p = i + 1;
                    else if (page <= 5) p = i + 1;
                    else if (page >= totalPages - 4) p = totalPages - 9 + i;
                    else p = page - 4 + i;
                    return (
                      <button key={p} className={page === p ? 'active' : ''} onClick={() => chargerBase(p)}>{p}</button>
                    );
                  })}
                  <button onClick={() => chargerBase(Math.min(totalPages, page + 1))} disabled={page === totalPages}>Suivant</button>
                </div>
              )}

              {repartition.parDept && repartition.parDept.length > 0 && (
                <div className="fichier-pro-db-stats">
                  <div className="db-stats-section">
                    <h4>Répartition du résultat</h4>
                    <div className="db-stats-tags">
                      {repartition.parDept.map(d => (
                        <span key={d.departement} className="db-stat-tag"
                          onClick={() => { setF('departement', d.departement); }}>
                          {d.departement} - {d.nom_departement} ({d.count})
                        </span>
                      ))}
                    </div>
                    <div className="db-stats-tags" style={{ marginTop: '8px' }}>
                      {(repartition.parStatut || []).map(s => (
                        <span key={s.statut} className="db-stat-tag">
                          {statutLabels[s.statut] || s.statut} ({s.count})
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">Aucun contact trouvé avec ces filtres.</div>
          )}
        </div>
      )}

      {/* Fiche client complète : tous les champs du fichier source */}
      {fiche && (
        <div className="modal-overlay" onClick={fermerFiche}>
          <div className="modal-content planning-modal" style={{ maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{[fiche.civilite, fiche.nom, fiche.prenom].filter(Boolean).join(' ') || 'Fiche contact'}</h3>
                <div className="dpv-fiche-header-sub">
                  {adresseComplete(fiche) || 'Adresse non renseignée'}
                  {fiche.departement ? ` · ${fiche.departement} ${fiche.nom_departement || ''}` : ''}
                </div>
              </div>
              <button className="btn-close" onClick={fermerFiche}>&times;</button>
            </div>

            <div className="dpv-fiche">
              {fiche.commentaire && (
                <div className="dpv-commentaire-bloc">
                  <div className="dpv-commentaire-titre">💬 Commentaire</div>
                  <div className="dpv-commentaire-texte">{fiche.commentaire}</div>
                </div>
              )}

              <div className="dpv-section">
                <div className="dpv-section-titre">Identité</div>
                <div className="dpv-grid">
                  <Champ label="Civilité" value={fiche.civilite} />
                  <Champ label="Nom" value={fiche.nom} />
                  <Champ label="Prénom" value={fiche.prenom} />
                  <Champ label="Date de naissance" value={fiche.date_naissance} />
                  <Champ label="Âge" value={fiche.age ? `${fiche.age} ans` : ''} />
                </div>
              </div>

              <div className="dpv-section">
                <div className="dpv-section-titre">Contact</div>
                <div className="dpv-grid">
                  <Champ label="Téléphone fixe">
                    {fiche.telephone
                      ? <a href={`tel:${fiche.telephone}`}>{fiche.telephone}</a>
                      : <span className="vide">Non renseigné</span>}
                  </Champ>
                  <Champ label="Téléphone portable">
                    {fiche.tel_portable
                      ? <a href={`tel:${fiche.tel_portable}`}>{fiche.tel_portable}</a>
                      : <span className="vide">Non renseigné</span>}
                  </Champ>
                  <Champ label="Email">
                    {fiche.email
                      ? <a href={`mailto:${fiche.email}`}>{fiche.email}</a>
                      : <span className="vide">Non renseigné</span>}
                  </Champ>
                  <Champ label="Marquage du fichier d'origine">
                    {fiche.doublon_tel
                      ? (fiche.doublon_tel === 'UNIQUE'
                        ? <span className="dpv-badge-unique">Numéro unique</span>
                        : <span className="dpv-badge-doublon" title="La base a été dédoublonnée : c'est la fiche conservée pour ce numéro">
                            {fiche.doublon_tel} — copie conservée
                          </span>)
                      : <span className="vide">Non renseigné</span>}
                  </Champ>
                </div>
              </div>

              <div className="dpv-section">
                <div className="dpv-section-titre">Adresse</div>
                <div className="dpv-grid">
                  <Champ label="N° de voie" value={fiche.adresse_num} />
                  <Champ label="Rue" value={fiche.adresse_rue} />
                  <Champ label="Code postal" value={fiche.code_postal} />
                  <Champ label="Ville" value={fiche.ville} />
                  <Champ label="Département" value={fiche.departement} />
                  <Champ label="Nom du département" value={fiche.nom_departement} />
                </div>
              </div>

              <div className="dpv-section">
                <div className="dpv-section-titre">Compteur &amp; consommation électrique</div>
                <div className="dpv-grid">
                  <Champ label="Numéro PDL" value={fiche.numero_pdl} />
                  <Champ label="Matricule compteur" value={fiche.matricule_compteur} />
                  <Champ label="Puissance souscrite" value={fiche.puissance_souscrite ? `${fiche.puissance_souscrite} kVA` : ''} />
                  <Champ label="Option tarifaire" value={fiche.option_tarifaire} />
                  <Champ label="Consommation annuelle" value={formatConso(fiche.conso_annuelle) || ''} />
                  <Champ label="Consommation totale retenue" value={formatConso(fiche.conso_totale) || ''} />
                  <Champ label="Consommation annuelle HC" value={formatConso(fiche.conso_annuelle_hc) || ''} />
                  <Champ label="Consommation annuelle HP" value={formatConso(fiche.conso_annuelle_hp) || ''} />
                  <Champ label="Horaires heures creuses" value={fiche.horaires_hc} />
                  <Champ label="Mode de paiement" value={fiche.mode_paiement} />
                  <Champ label="Canal d'origine" value={fiche.canal} />
                </div>
              </div>

              <div className="dpv-section">
                <div className="dpv-section-titre">Facture annuelle estimée</div>
                {fiche.detail_estimation ? (
                  <>
                    <div className="dpv-facture">
                      <table className="dpv-facture-table">
                        <tbody>
                          {fiche.detail_estimation.lignes.map((l, i) => (
                            <tr key={i}>
                              <td>{l.libelle}</td>
                              <td className="dpv-facture-detail">
                                {l.kwh.toLocaleString('fr-FR')} kWh × {formatEuros(l.prix, 4)}
                              </td>
                              <td className="dpv-facture-montant">{formatEuros(l.montant, 2)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td>Abonnement</td>
                            <td className="dpv-facture-detail">
                              {fiche.detail_estimation.puissance
                                ? `${fiche.detail_estimation.puissance} kVA · option ${fiche.detail_estimation.option}`
                                : `puissance inconnue — abonnement 6 kVA retenu · option ${fiche.detail_estimation.option}`}
                            </td>
                            <td className="dpv-facture-montant">{formatEuros(fiche.detail_estimation.abonnement, 2)}</td>
                          </tr>
                          <tr className="dpv-facture-total">
                            <td>Total annuel estimé</td>
                            <td className="dpv-facture-detail">
                              soit {formatEuros(fiche.detail_estimation.total_mensuel, 2)} par mois
                            </td>
                            <td className="dpv-facture-montant">{formatEuros(fiche.detail_estimation.total_annuel, 2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="dpv-avertissement">
                      Estimation calculée à partir de la consommation du fichier et du barème en vigueur
                      {tarifs ? ` (« ${tarifs.libelle} »)` : ''} — ce n'est pas une facture réelle.
                      {!fiche.detail_estimation.abonnement_estime && ' La puissance souscrite étant absente de la fiche, l\'abonnement 6 kVA a été retenu par défaut.'}
                    </p>
                  </>
                ) : (
                  <p className="dpv-avertissement">
                    Aucune consommation dans le fichier pour ce contact : la facture ne peut pas être estimée.
                  </p>
                )}
              </div>

              <div className="dpv-section">
                <div className="dpv-section-titre">Origine de la donnée</div>
                <div className="dpv-grid">
                  <Champ label="Fichier source" value={fiche.source_fichier} />
                  <Champ label="Onglet source" value={fiche.source_onglet} />
                  <Champ label="Ligne d'origine" value={fiche.ligne_origine} />
                  <Champ label="Ajouté au CRM" value={fiche.date_ajout ? formatDateHeure(fiche.date_ajout) : ''} />
                </div>
              </div>

              <div className="dpv-section">
                <div className="dpv-section-titre">Suivi</div>
                <div className="dpv-grid">
                  <Champ label="Statut">
                    <select className={`statut-select statut-${fiche.statut || 'nouveau'}`}
                      value={fiche.statut || 'nouveau'}
                      onChange={e => handleStatutChange(fiche.id, e.target.value)}>
                      {Object.entries(statutLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Champ>
                  <Champ label="Statué par"
                    value={fiche.statut_par_nom ? `${fiche.statut_par_nom} — ${formatDateHeure(fiche.statut_date)}` : ''} />
                </div>
                <div className="dpv-comment-zone" style={{ marginTop: '12px' }}>
                  <span className="dpv-field-label">
                    {fiche.commentaire ? 'Modifier le commentaire' : 'Ajouter un commentaire'}
                  </span>
                  <textarea value={commentDraft} onChange={e => setCommentDraft(e.target.value)}
                    placeholder="Ajouter un commentaire sur ce contact..." />
                  <div className="dpv-comment-actions">
                    {commentEnregistre && <span className="dpv-comment-ok">Commentaire enregistré ✓</span>}
                    {!commentEnregistre && commentDraft !== (fiche.commentaire || '') && (
                      <span className="dpv-comment-attente">Modification non enregistrée</span>
                    )}
                    <button className="btn-search" onClick={handleSaveComment}
                      disabled={savingComment || commentDraft === (fiche.commentaire || '')}>
                      {savingComment ? <><span className="spinner-small"></span> Enregistrement...</> : 'Enregistrer le commentaire'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Réglage du barème servant à l'estimation */}
      {showTarifs && tarifsDraft && (
        <div className="modal-overlay" onClick={() => setShowTarifs(false)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Barème d'estimation</h3>
              <button className="btn-close" onClick={() => setShowTarifs(false)}>&times;</button>
            </div>
            <div className="dpv-fiche">
              <p className="dpv-avertissement">
                Ces montants servent uniquement à estimer la facture des contacts à partir de leur consommation.
                Les valeurs livrées sont un ordre de grandeur type Tarif Bleu TTC : remplacez-les par vos propres
                tarifs pour obtenir des estimations justes.
              </p>

              <div className="dpv-section">
                <div className="dpv-section-titre">Prix du kWh (TTC)</div>
                <div className="dpv-grid">
                  {[['prix_base', 'Option Base'], ['prix_hp', 'Heures pleines'], ['prix_hc', 'Heures creuses']].map(([champ, label]) => (
                    <div className="dpv-field" key={champ}>
                      <span className="dpv-field-label">{label}</span>
                      <input type="number" step="0.0001" min="0" className="filter-input"
                        value={tarifsDraft[champ]}
                        onChange={e => setTarifsDraft({ ...tarifsDraft, [champ]: e.target.value })} />
                    </div>
                  ))}
                </div>
              </div>

              {[['base', 'Abonnement annuel — option Base'], ['hphc', 'Abonnement annuel — option HP-HC']].map(([cle, titre]) => (
                <div className="dpv-section" key={cle}>
                  <div className="dpv-section-titre">{titre}</div>
                  <div className="dpv-grid">
                    {Object.keys(tarifsDraft.abonnements[cle]).map(kva => (
                      <div className="dpv-field" key={kva}>
                        <span className="dpv-field-label">{kva} kVA</span>
                        <input type="number" step="0.01" min="0" className="filter-input"
                          value={tarifsDraft.abonnements[cle][kva]}
                          onChange={e => setTarifsDraft({
                            ...tarifsDraft,
                            abonnements: {
                              ...tarifsDraft.abonnements,
                              [cle]: { ...tarifsDraft.abonnements[cle], [kva]: e.target.value }
                            }
                          })} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="dpv-section">
                <div className="dpv-section-titre">Intitulé du barème</div>
                <input type="text" className="filter-input" style={{ width: '100%' }}
                  value={tarifsDraft.libelle} maxLength={150}
                  onChange={e => setTarifsDraft({ ...tarifsDraft, libelle: e.target.value })} />
                {tarifs && tarifs.maj_le && (
                  <p className="dpv-avertissement">
                    Dernière modification : {formatDateHeure(tarifs.maj_le)}
                    {tarifs.maj_par_nom ? ` par ${tarifs.maj_par_nom}` : ''}.
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowTarifs(false)}>Annuler</button>
                <button type="button" className="btn-submit" onClick={enregistrerTarifs} disabled={savingTarifs}>
                  {savingTarifs ? 'Enregistrement...' : 'Enregistrer et recalculer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataPacPv;
